import angular from 'angular';
import DataCacheService from '@/shared/services/DataCacheService';
import { setEnumCache } from '@/shared/services/EnumCacheService';
import SignalRService from '@/shared/services/SignalRService';
import EmployeeFilterService from '@/shared/services/EmployeeFilterService';
import { guid1 } from '@/shared/utils/guid';

const commonCountriesPerConfiguration = {
  0: ['NL', 'BE', 'FR', 'LU', 'DE', 'GB', 'US', 'CH', 'IT'],
  1: ['NL', 'BE', 'FR', 'LU', 'DE', 'GB', 'US', 'CH', 'IT'],
  3: ['NL', 'BE', 'FR', 'LU', 'GB', 'CH', 'DE', 'US'],
  5: ['NL', 'BE', 'DE', 'US'],
};
const mapStaticCacheItems = (key, data, tenantConfiguration) => {
  switch (key) {
    case 'country': {
      const commonCountries = commonCountriesPerConfiguration[tenantConfiguration] || [];
      return data.map(x => ({
        ...x,
        isCommon: commonCountries.indexOf(x.code.toUpperCase()) >= 0,
      }));
    }
    case 'currency':
      return data.map(x => ({
        ...x,
        isCommon: x.code.toUpperCase() === 'EUR',
      }));

    default:
      return data;
  }
};

angular
  .module('commonServices')
  .service('dataCache', [
    '$http',
    'appUrls',
    '$q',
    '$timeout',
    'enumCacheSvc',
    'authService',
    '$injector',
    'recentRelationsSvc',
    '$translate',
    '$rootScope',
    'buildversion',
    '$document',
    dataCache,
  ]);

function dataCache(
  $http,
  appUrls,
  $q,
  $timeout,
  enumCacheSvc,
  authService,
  $injector,
  recentRelationsSvc,
  $translate,
  $rootScope,
  buildversion,
  $document,
) {
  var cache = this;

  var lock = null;
  let naceCodeLanguage = null;

  // In cache, we store collections, but we name them single because we use the same var in the load()fn to get the resource and in filters and selects...
  cache.ready = false;

  const documentSigningProviderUpdatedHandler = (companyId, documentSigningProvider) => {
    $timeout(function() {
      var dataCacheCompany = cache['company'].find(x => x.id == companyId);
      if (dataCacheCompany) {
        const model = {
          ...dataCacheCompany,
          documentSigningProvider: documentSigningProvider,
        };
        angular.extend(dataCacheCompany, model);
      }
    }, 100);
  };

  const employeeConnectPlatformsUpdatedHandler = employeeConnectPlatforms => {
    $timeout(function() {
      var datacacheEmployee = cache['employee'];
      if (datacacheEmployee) {
        const model = {
          ...datacacheEmployee,
          configuredConnectPlatformsCommaSeparated: employeeConnectPlatforms,
        };
        angular.extend(datacacheEmployee, model);
      }
    }, 100);
  };

  var createObjectFromArray = function(objectType, data) {
    switch (objectType) {
      case 'legalForm':
      case 'company':
      case 'salutation': {
        return data.reduce(function(o, v, i) {
          o[v.id] = v;
          return o;
        }, {});
      }
      case 'user':
        return data.reduce(function(o, v) {
          if (v.userID) o[v.userID] = v;
          return o;
        }, {});
      default:
        return null;
    }
  };

  var loadUserData = function() {
    var deferred = $q.defer();
    const staticDataRequest = $http.get(`/assets/data/dataCache.json?b=${buildversion}`);
    const userDataRequest = $http.get(`${appUrls.api}/api/user/data`);

    const tenantConfiguration = parseInt(authService.getClaims().TenantConfiguration || '1');
    $q.all([userDataRequest, staticDataRequest]).then(
      ([{ data: userData = {} }, { data: staticData = {} }]) => {
        angular.forEach(staticData, (value, key, obj) => {
          cache[key] = mapStaticCacheItems(key, value, tenantConfiguration);
          cache[key + 'Object'] = createObjectFromArray(key, cache[key]);
        });

        // put all the data in the cache object
        angular.forEach(userData, function(value, key, obj) {
          if (cache[key]) {
            console.warn(`Key ${key} already exists in dataCache`);
            return;
          }
          cache[key] = value;
          cache[key + 'Object'] = createObjectFromArray(key, value);
        });

        cache['defaultcompany'] = cache['company'].find(x => x.isDefaultCompany);
        const systemUserName = $translate.instant('SystemUser');
        cache.systemUser = {
          id: guid1,
          name: systemUserName,
          label: systemUserName,
          languageCode: $translate.use(),
          isActive: true,
        };
        if (authService.getClaims().su) {
          $document[0].title = `${cache['defaultcompany'].name}`;
        }

        loadNaceCodes().then(
          () => {
            DataCacheService.setDataCache(cache);
            deferred.resolve(true);
          },
          () => {
            DataCacheService.setDataCache(cache);
            deferred.resolve(true);
          },
        );
      },
      error => {
        deferred.reject(error);
        console.error('Loading application data FAILED...');
      },
    );
    return deferred.promise;
  };

  var ready = function() {
    if (lock != null) return lock.promise;
    lock = $q.defer();

    recentRelationsSvc.reloadRecentRelations();
    EmployeeFilterService.load();

    if (!cache.ready) {
      loadUserData().then(
        function() {
          enumCacheSvc.init(cache.buildNumber + ';' + cache.tenantConfiguration).then(function() {
            // when all these promises are resolved
            $injector.get('timerSvc').init();
            setEnumCache(enumCacheSvc);

            lock.resolve(cache);
            cache.ready = true;
          });
        },
        function(error) {
          cache.ready = false;
          if (lock != null) lock.reject(error);
          lock = null;
        },
      );
    } else {
      lock.resolve(cache);
    }
    return lock.promise;
  };
  ready();

  const loadNaceCodes = () => {
    const languageCode = $translate.use().split('-')[0];
    if (naceCodeLanguage === languageCode) return Promise.resolve();
    naceCodeLanguage = languageCode;
    const naceCodesRequest = $http.get(`/assets/data/nace-${languageCode}.json?b=${buildversion}`);

    return naceCodesRequest.then(({ data }) => {
      cache.naceCodes = data;
    });
  };

  const languageChanged = function() {
    if (!cache || !cache.ready || !cache.systemUser) return;
    cache.systemUser.name = $translate.instant('SystemUser');
    cache.systemUser.label = cache.systemUser.name;
    cache.systemUser.languageCode = $translate.use();
    loadNaceCodes();
  };
  var translateListenerDestroyer = $rootScope.$on('$translateChangeSuccess', languageChanged);

  var destroy = function() {
    cache = {
      ready: false,
    };
    lock = null;
    translateListenerDestroyer();
  };

  authService.addOnLogOut(function() {
    destroy();
  });

  SignalRService.subscribe('documentSigningProviderUpdated', documentSigningProviderUpdatedHandler);

  SignalRService.subscribe(
    'employeeConnectPlatformsUpdated',
    employeeConnectPlatformsUpdatedHandler,
  );

  let getDefaultCompanyForUser = (userId = null) => {
    if (!cache) return null;
    const employee = userId ? cache['user'].find(x => x.userID === userId) : cache['employee'];
    const companyId = employee?.companyId;
    return companyId ? cache['company'].find(x => x.id === companyId) : cache['defaultcompany'];
  };

  return {
    ready: ready,
    getArray: function(type) {
      return cache[type];
    },
    getObject: function(type) {
      return cache[type + 'Object'];
    },
    update: function(type, value) {
      cache[type] = value;
    },
    getCompanySettings: function(companyId) {
      if (!cache) return {};
      const companies = cache['company'] || [];
      const company = companies.find(x => x.id == companyId);

      if (!company) {
        console.warn('Company with id ' + companyId + ' not found in data cache');
        return {};
      }

      return company;
    },
    showPaymentMonitoring: function() {
      // the menu item for transactions etc should be shown if at least 1 company has this setting enabled
      if (!cache) return false;
      const companies = cache['company'] || [];
      return companies.some(x => x.paymentMonitoring === true);
    },
    getDefaultCompany: function() {
      if (!cache) return null;
      return cache['defaultcompany'];
    },

    updateCompany: function(updatedCompany) {
      if (!cache) return;
      var oldCompany = cache['company'].find(x => x.id == updatedCompany.id);
      angular.extend(oldCompany, updatedCompany);
      if (updatedCompany.tenantId === updatedCompany.id) {
        cache['defaultcompany'] = updatedCompany;
      }
    },
    updateAllCompanies: function(data) {
      if (!cache || !data) return;
      cache['company'].forEach(company => {
        angular.extend(company, data);
      });
    },
    getDefaultCompanyForUser,
    getAbsenceRelationIdForUser: (userId = null) =>
      getDefaultCompanyForUser(userId)?.absenceRelationId,
  };
}
